import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { IScormLearnState, IScormOpenAsset } from 'src/app/interfaces/iscorm-learn-state';
import { IScormLearnerContext } from 'src/app/interfaces/iscorm-learner-context';
import { ICallScormChild } from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import {
  AudioCallControlService,
  ConversationState,
  ISegmentProvider,
} from 'src/app/services/learner/audio-call-control.service';
import { PdfViewerComponent } from 'src/app/components/shared/pdf-viewer/pdf-viewer.component';
import { QaiButtonComponent, QaiIconComponent, QaiPageIndicatorComponent } from '@qai/ui';
import { ScormApi } from './scorm';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { ScormBridge } from './scorm-bridge';

interface ActiveAsset {
  kind: 'pdf' | 'video';
  url: string;
  fileName: string;
  contentUuid?: string; // matched embedded child's customer_content uuid
  page?: number; // pdf only
  positionMs?: number; // video only
}

// Coalesce the Commit-then-Terminate burst on exit into a single write.
const PERSIST_DEBOUNCE_MS = 150;
// Video position updates fire on every timeupdate tick — throttle how often
// we write them so we're not hammering the backend while a video plays.
const VIDEO_PERSIST_THROTTLE_MS = 4000;

@Component({
  selector: 'app-call-pathway-scorm',
  standalone: true,
  imports: [PdfViewerComponent, QaiButtonComponent, QaiIconComponent, QaiPageIndicatorComponent],
  templateUrl: './call-pathway-scorm.component.html',
  styleUrl: './call-pathway-scorm.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallPathwayScormComponent implements OnInit, OnDestroy, ISegmentProvider {
  scormUrl = input.required<string>();
  enrollmentUuid = input.required<string>();
  instanceUuid = input.required<string>(); // step instance uuid — key instance_progress is stored under
  callUuid = input<string>(''); // converse: CONVERSE prompt + linked SCORM content
  practiceCallUuid = input<string>(''); // converse: auth
  initialState = input<IScormLearnState | null>(null);
  packageContentUuid = input<string>(''); // learnerContext.packageCustomerContentUuid
  children = input<ICallScormChild[]>([]); // embedded-asset catalog
  // True until the learner has completed this step once — gates forward-skip
  // prevention on the embedded video overlay, same as the standalone video step.
  firstAttempt = input<boolean>(false);

  scormCompleted = output<void>();

  // Embedded PDF/video in the overlay. Persisted alongside the CMI as
  // openAsset (keyed by contentUuid) so resuming reopens the same page/time;
  // the SCORM course itself still resumes separately via cmi.location/suspend_data.
  protected readonly activeAsset = signal<ActiveAsset | null>(null);

  protected safeScormUrl = computed<SafeResourceUrl>(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(this.scormUrl())
  );

  // Embedded PDF viewer chrome — page nav + page indicator. Mirrors
  // CallPathwayPdfComponent's controls, trimmed to what the overlay needs
  // (no zoom/fullscreen/scroll-gating/audio-call, which belong to the
  // standalone Learn-with-PDF step, not an asset opened from within the
  // SCORM package).
  protected readonly embeddedTotalPages = signal(0);
  protected readonly embeddedCurrentPage = signal(1);
  protected readonly embeddedScrollbarGapPx = signal(0);
  private readonly embeddedPdfViewer = viewChild(PdfViewerComponent);
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  private readonly pathwaysService = inject(PathwaysService);
  private readonly callStore = inject(CallStoreService);
  private readonly audioCallControlService = inject(AudioCallControlService);
  private readonly zone = inject(NgZone);
  private readonly scormFrame = viewChild<ElementRef<HTMLIFrameElement>>('scormFrame');
  private readonly hostVideo = viewChild<ElementRef<HTMLVideoElement>>('hostVideo');
  private readonly pdfViewer = viewChild(PdfViewerComponent);

  private scorm?: ScormApi;
  private bridge?: ScormBridge;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private audioCallStarted = false;
  private lastVideoPersistAt = 0;
  // Furthest position genuinely reached in the CURRENTLY open video (ms), keyed
  // implicitly to activeAsset().contentUuid. Seeds skip-prevention (firstAttempt)
  // and guards against a re-fired video-expand handoff (e.g. the package
  // re-expanding on its own load) regressing an already-resumed position back
  // toward whatever currentTime the in-package video reports at that moment.
  private maxReachedPositionMs = 0;
  private isClampingSeek = false;
  private seekClampFrame: number | null = null;
  // Many SCORM packages SetValue + Commit the completion/success status once
  // the learner reaches the end screen, but never call Terminate (that's tied
  // to an explicit exit/unload the embedded player never triggers here) — so
  // completion must be detected on Commit too, not just Terminate. Guarded so
  // the Next-button-unlock output only fires once per step.
  private completionEmitted = false;
  // No audio overlap: pause the embedded video whenever the coach or learner
  // is speaking, and block resuming it (native play button) until they're
  // done — mirrors CallPathwayVideoComponent's pauseVideo()/handleVideoPlay()
  // guard; manual resume only, no auto-resume, for consistency with that step.
  private isAnySpeaking = false;
  private pauseOnSpeakingSubscription?: Subscription;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.scorm = new ScormApi(this.initialState(), {
      onProgress: (state) => {
        this.persistState();
        this.emitCompletionIfPassed(state);
      },
      onTerminate: (finalState) => {
        this.persistState();
        this.emitCompletionIfPassed(finalState);
      },
    });
    (window as any).API_1484_11 = this.scorm.api;

    this.resumeOpenAsset();
    this.startAudioCallOnce();
    this.audioCallControlService.registerSegmentProvider(this);
    this.pauseOnSpeakingSubscription = this.audioCallControlService.onStateChange$.subscribe(
      (state) => {
        this.isAnySpeaking =
          state === ConversationState.AGENT_SPEAKING || state === ConversationState.USER_SPEAKING;
        if (this.isAnySpeaking) this.pauseVideo();
      }
    );
  }

  // Pauses the overlay and in-package video; public so the parent can call it too, mirroring CallPathwayVideoComponent.pauseVideo().
  public pauseVideo(): void {
    this.hostVideo()?.nativeElement.pause();
    this.bridge?.pauseVideos();
  }

  // Re-pauses if resumed while someone's speaking; otherwise mutes the mic, mirroring CallPathwayVideoComponent.handleVideoPlay().
  protected onHostVideoPlay(video: HTMLVideoElement): void {
    if (this.isAnySpeaking) {
      video.pause();
      return;
    }
    this.audioCallControlService.setMicMuted(true).catch((err) => {
      console.error('setMicMuted(true) on SCORM video play failed', err);
    });
  }

  private emitCompletionIfPassed(state: IScormLearnState): void {
    if (this.completionEmitted) return;
    if (state.completion_status === 'completed' || state.success_status === 'passed') {
      this.completionEmitted = true;
      this.scormCompleted.emit();
    }
  }

  // Reopen the PDF page / video timestamp the learner had open when they left,
  // resolving the child fresh from children() rather than trusting a stored URL.
  // Skipped when completed: the SCORM course itself resumes from the start in
  // that case (scorm.ts), so the embedded overlay shouldn't reopen either.
  private resumeOpenAsset(): void {
    if (
      this.initialState()?.completion_status === PathwayConstants.SCORM_COMPLETION_STATUS_COMPLETED
    )
      return;
    const savedAsset = this.initialState()?.openAsset;
    if (!savedAsset) return;
    const child = this.children().find((c) => c.uuid === savedAsset.contentUuid);
    if (!child) return;
    this.activeAsset.set({
      kind: savedAsset.kind,
      url: child.s3Location ?? '',
      fileName: child.fileName,
      contentUuid: child.uuid,
      page: savedAsset.kind === 'pdf' ? savedAsset.page : undefined,
      positionMs: savedAsset.kind === 'video' ? savedAsset.positionMs : undefined,
    });
    if (savedAsset.kind === 'video') {
      this.maxReachedPositionMs = savedAsset.positionMs ?? 0;
    }
  }

  private startAudioCallOnce(): void {
    if (this.audioCallStarted) return;
    this.audioCallStarted = true;
    this.callStore.setIsAudioCallMode(true);
    this.audioCallControlService
      .startCall()
      .then(() =>
        this.audioCallControlService.setMicMuted(!this.callStore.getUserMicPreference())
      )
      .catch((err) => {
        console.error('Audio call start failed', err);
        this.audioCallStarted = false;
      });
  }

  // Consume the injected bridge's embedded-asset handoffs over postMessage.
  protected onIframeLoad(): void {
    const iframe = this.scormFrame()?.nativeElement;
    if (!iframe || this.bridge) return;
    this.bridge = new ScormBridge(iframe, {
      onPdf: (url, fileName) => this.zone.run(() => this.openAsset('pdf', url, fileName)),
      onVideo: (src, currentTime) =>
        this.zone.run(() => this.openAsset('video', src, '', currentTime)),
    });
    this.bridge.attach();
    // A PDF resumed before the bridge attached may sit over an in-package
    // video that started playing on load — silence it now that we can.
    if (this.activeAsset()?.kind === 'pdf') this.bridge.pauseVideos();
  }

  // Where the learner is now — sent each voice turn (position rides in the utterance).
  learnerContext(): IScormLearnerContext {
    const asset = this.activeAsset();
    if (!asset) {
      return { kind: 'course', packageCustomerContentUuid: this.packageContentUuid() };
    }
    return {
      kind: asset.kind,
      packageCustomerContentUuid: this.packageContentUuid(),
      focusCustomerContentUuid: asset.contentUuid,
    };
  }

  // ISegmentProvider — position-prefixed utterance for the voice turn. The FE
  // owns the prefix: PDF page number, video mm:ss, or bare in the course shell.
  getUserSpokenResponse(): string {
    const text =
      (this.callStore.getSpeechTranscriptPartial() ?? '').trim() ||
      (this.callStore.getSpeechTranscript() ?? '').trim();
    return this.buildUtterance(text);
  }

  // SCORM content is linked via customer_content, not call_learn.
  getCallLearnUuid(): string {
    return '';
  }

  // ISegmentProvider — supplying this routes the voice turn to SCORM converse
  // (processContentQuestion + learnerContext) instead of Learn's processUserResponse.
  getLearnerContext(): IScormLearnerContext {
    return this.learnerContext();
  }

  buildUtterance(text: string): string {
    const asset = this.activeAsset();
    if (asset?.kind === 'pdf' && asset.page != null) {
      return `${asset.page} | ${text}`;
    }
    if (asset?.kind === 'video') {
      return `${this.formatMmSs(asset.positionMs ?? 0)} | ${text}`;
    }
    return text;
  }

  // Milliseconds → m:ss (or h:mm:ss past the hour), matching the Learn coach convention.
  formatMmSs(positionMs: number): string {
    const total = Math.max(0, Math.floor(positionMs / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const ss = String(seconds).padStart(2, '0');
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
    }
    return `${minutes}:${ss}`;
  }

  private openAsset(
    kind: 'pdf' | 'video',
    url: string,
    fileName: string,
    currentTime = 0
  ): void {
    const child = this.matchChild(url, fileName);
    const prev = this.activeAsset();
    const isSameVideoReopened =
      kind === 'video' && !!child && prev?.kind === 'video' && prev.contentUuid === child.uuid;
    // A video-expand handoff can re-fire for the same video (e.g. the package
    // re-expanding it on its own load) carrying the in-package video's OWN
    // currentTime, which may be behind what we already know was reached
    // (persisted resume, or natural playback since). Never regress.
    const incomingPositionMs = kind === 'video' ? Math.round(currentTime * 1000) : undefined;
    const positionMs = isSameVideoReopened
      ? Math.max(this.maxReachedPositionMs, incomingPositionMs ?? 0)
      : incomingPositionMs;
    if (kind === 'video') {
      this.maxReachedPositionMs = isSameVideoReopened ? positionMs ?? 0 : incomingPositionMs ?? 0;
    }
    this.activeAsset.set({
      kind,
      url,
      fileName: child?.fileName ?? fileName,
      contentUuid: child?.uuid,
      page: kind === 'pdf' ? 1 : undefined,
      positionMs,
    });
    // Opening a PDF over the course: silence any in-package video behind it.
    if (kind === 'pdf') {
      this.bridge?.pauseVideos();
      this.embeddedTotalPages.set(0);
      this.embeddedCurrentPage.set(1);
      this.embeddedScrollbarGapPx.set(0);
    }
    this.logLearnerContext();
    this.persistState();
  }

  // Jump to the resumed page once the viewer knows its page count
  protected onPdfPagesLoaded(total: number): void {
    const asset = this.activeAsset();
    if (asset?.kind !== 'pdf' || !asset.page || asset.page <= 1 || asset.page > total) return;
    const page = asset.page;
    requestAnimationFrame(() => this.pdfViewer()?.goToPage(page));
  }

  protected onPdfPageChange(page: number): void {
    const asset = this.activeAsset();
    if (asset?.kind !== 'pdf' || asset.page === page) return;
    this.activeAsset.set({ ...asset, page });
    this.embeddedCurrentPage.set(page);
    this.persistState();
  }

  protected onEmbeddedPagesLoaded(total: number): void {
    this.embeddedTotalPages.set(total);
  }

  protected onEmbeddedScrollableChange(scrollable: boolean): void {
    if (!scrollable) {
      this.embeddedScrollbarGapPx.set(0);
      return;
    }
    const container = this.hostRef.nativeElement.querySelector('.viewer-container') as HTMLElement | null;
    this.embeddedScrollbarGapPx.set(container ? container.offsetWidth - container.clientWidth : 0);
  }

  protected previousEmbeddedPage(): void {
    if (this.canGoPreviousEmbedded) this.embeddedPdfViewer()?.goToPreviousPage();
  }

  protected nextEmbeddedPage(): void {
    if (this.canGoNextEmbedded) this.embeddedPdfViewer()?.goToNextPage();
  }

  protected get canGoPreviousEmbedded(): boolean {
    return this.embeddedCurrentPage() > 1;
  }

  protected get canGoNextEmbedded(): boolean {
    return this.embeddedCurrentPage() < this.embeddedTotalPages();
  }

  protected onVideoTimeUpdate(currentTimeSec: number): void {
    const asset = this.activeAsset();
    if (asset?.kind !== 'video') return;
    const positionMs = Math.round(currentTimeSec * 1000);
    this.activeAsset.set({ ...asset, positionMs });
    if (!this.isClampingSeek && positionMs > this.maxReachedPositionMs) {
      this.maxReachedPositionMs = positionMs;
    }
    const now = Date.now();
    if (now - this.lastVideoPersistAt < VIDEO_PERSIST_THROTTLE_MS) return;
    this.lastVideoPersistAt = now;
    this.persistState();
  }

  // Snap a forward scrub back to the furthest genuinely-reached position on
  // first attempt — mirrors QaiCallPathwayVideoComponent's preventForwardSeek,
  // needed here too since this overlay keeps the native <video> scrubber.
  protected onHostVideoSeeking(video: HTMLVideoElement): void {
    if (!this.firstAttempt() || this.isClampingSeek) return;
    const maxReachedSec = this.maxReachedPositionMs / 1000;
    if (video.currentTime <= maxReachedSec) return;
    this.isClampingSeek = true;
    if (this.seekClampFrame !== null) cancelAnimationFrame(this.seekClampFrame);
    this.seekClampFrame = requestAnimationFrame(() => {
      video.currentTime = maxReachedSec;
      this.isClampingSeek = false;
      this.seekClampFrame = null;
    });
  }

  // Start the host overlay video where the in-package video left off.
  protected onHostVideoLoaded(video: HTMLVideoElement): void {
    const asset = this.activeAsset();
    if (asset?.kind !== 'video') return;
    const startSec = (asset.positionMs ?? 0) / 1000;
    if (startSec > 0) {
      try {
        video.currentTime = startSec;
      } catch {
        /* ignore */
      }
    }
  }

  protected closeDialog(): void {
    const asset = this.activeAsset();
    if (asset?.kind === 'video') {
      // Re-sync the in-package video to where the learner left off and release it.
      const currentTime = this.hostVideo()?.nativeElement.currentTime ?? 0;
      this.bridge?.seekVideo(asset.url, currentTime);
    }
    this.activeAsset.set(null);
    this.logLearnerContext();
    this.persistState();
  }

  private persistState(): void {
    if (!this.scorm) return;
    if (this.persistTimer !== null) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.writeProgress();
    }, PERSIST_DEBOUNCE_MS);
  }

  // Persist the SCORM course CMI state plus which embedded PDF/video is open.
  private writeProgress(): void {
    const instanceUuid = this.instanceUuid();
    if (!instanceUuid || !this.scorm) return;
    const state = { ...this.scorm.getState(), openAsset: this.buildOpenAssetSnapshot() };
    this.pathwaysService.updateInstanceProgress(instanceUuid, state).subscribe();
  }

  // null once the overlay is closed, so a later resume doesn't reopen it.
  private buildOpenAssetSnapshot(): IScormOpenAsset | null {
    const asset = this.activeAsset();
    if (!asset?.contentUuid) return null;
    const snapshot: IScormOpenAsset = { kind: asset.kind, contentUuid: asset.contentUuid };
    if (asset.kind === 'pdf' && asset.page != null) snapshot.page = asset.page;
    if (asset.kind === 'video' && asset.positionMs != null) snapshot.positionMs = asset.positionMs;
    return snapshot;
  }

  // Resolve the child: s3Location↔url path match first (video sends no file name), then file name exact / unambiguous suffix.
  private matchChild(url: string, fileName: string): ICallScormChild | undefined {
    if (url) {
      const byUrl = this.children().find(
        (c) => c.s3Location && this.sameAsset(c.s3Location, url)
      );
      if (byUrl) return byUrl;
    }
    const exact = fileName
      ? this.children().find((c) => c.fileName === fileName)
      : undefined;
    if (exact) return exact;
    if (!fileName) return undefined;
    const suffixMatches = this.children().filter(
      (c) => c.fileName && c.fileName.endsWith(fileName)
    );
    return suffixMatches.length === 1 ? suffixMatches[0] : undefined;
  }

  // Same asset when the URL paths match, ignoring query/hash
  private sameAsset(a: string, b: string): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    const pa = this.assetPath(a);
    return pa !== '' && pa === this.assetPath(b);
  }

  private assetPath(u: string): string {
    try {
      return new URL(u, window.location.origin).pathname;
    } catch {
      return '';
    }
  }

  private logLearnerContext(): void {
    console.log('[SCORM] learnerContext', this.learnerContext());
  }

  ngOnDestroy(): void {
    this.bridge?.detach();
    if (this.audioCallStarted) {
      this.audioCallControlService.endCall().catch((err) => {
        console.error('Audio call end failed on destroy', err);
      });
    }
    this.audioCallControlService.unregisterSegmentProvider();
    this.pauseOnSpeakingSubscription?.unsubscribe();
    if (this.seekClampFrame !== null) {
      cancelAnimationFrame(this.seekClampFrame);
      this.seekClampFrame = null;
    }
    // Flush the pending debounced save now — covers leaving the step early.
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    this.writeProgress();
    delete (window as any).API_1484_11;
  }
}
